// Layer 2 (pure): scenario bundles and perturbation resolution — no engine imports.
// See docs/engines/spc.md § Lines of separation (rev. 2).

import type {
  Perturbation,
  ScenarioBundle,
  ScenarioId,
  SessionState,
  WhatIfInputs,
} from '@/versions/v1/schema/scenario';
import { WHAT_IF_DEFAULTS } from '@/versions/v1/schema/scenario';

export const SCENARIOS: readonly ScenarioBundle[] = [
  {
    id: 'hardness-step-change',
    label: 'Hardness rising → Coarse grind confirmed',
    description:
      'JT-023 kW/tph rising while WIT-021 holds — hardness, not media wear; SA01 P80 lands at 88 µm.',
    perturbations: [
      {
        kind: 'ramp',
        seriesId: 'rs-jt-023',
        from: '2026-06-08T08:00',
        perHour: 0.35,
        cap: 2.0,
      },
      { kind: 'set', seriesId: 'rs-sa01-p80', from: '2026-06-08T13:00', value: 88 },
    ],
    stops: { provisionalAt: '2026-06-08T11:45', confirmsAt: '2026-06-08T13:15' },
    provenance: '[syn] Module 1 §8.5 hardness walkthrough',
  },
  {
    id: 'media-depletion',
    label: 'Media depletion → Mill charge worn',
    description:
      'JT-023 kW/tph falling while WIT-021 holds and PSA coarsens — media wear, not hardness; dr-media wins over dr-hardness.',
    perturbations: [
      {
        kind: 'ramp',
        seriesId: 'rs-jt-023',
        from: '2026-06-08T06:00',
        perHour: -0.35,
        cap: 2.0,
      },
      { kind: 'set', seriesId: 'rs-sa01-p80', from: '2026-06-08T08:00', value: 88 },
      {
        kind: 'step',
        seriesId: 'rs-pb-ph',
        from: '2026-06-08T08:00',
        delta: -1.5,
      },
    ],
    provenance: '[syn] FMEA fm-apex media row · consume.K5 rival separation',
  },
  {
    id: 'silver-push',
    label: 'Push silver',
    description:
      'Mine pushes Ag grade +3 oz/t; Pb circuit loads and Ag recovery ramps down — revenue lever, plant penalty.',
    perturbations: [
      { kind: 'step', seriesId: 'rs-ag-grade', from: '2026-06-06T18:00', delta: 3.0 },
      {
        kind: 'ramp',
        seriesId: 'rs-ag-rec',
        from: '2026-06-06T18:00',
        perHour: -0.02,
        cap: 1.2,
      },
    ],
    provenance: '[syn] Paul A16 — silver push seed',
  },
];

export const HARDNESS = SCENARIOS.find((s) => s.id === 'hardness-step-change')!;

export function perturbationsFor(state: SessionState): readonly Perturbation[] {
  const out: Perturbation[] = [];
  for (const id of state.scenarios) {
    const bundle = SCENARIOS.find((s) => s.id === id);
    if (bundle) out.push(...bundle.perturbations);
  }
  if (state.whatIf) {
    const scale = state.whatIf.feedStph / WHAT_IF_DEFAULTS.feedStph;
    out.push(
      { kind: 'scale', seriesId: 'rs-wit-021', from: state.cursor, factor: scale },
      { kind: 'scale', seriesId: 'rs-jt-023', from: state.cursor, factor: state.whatIf.hardness },
    );
  }
  return out;
}

/** Series ids touched by the active scenario bundle — for `[syn]` on perturbed reads. */
export function perturbedSeriesIds(state: SessionState): Set<string> {
  const ids = new Set<string>();
  for (const p of perturbationsFor(state)) {
    if ('seriesId' in p) ids.add(p.seriesId);
  }
  return ids;
}

export type { ScenarioId, WhatIfInputs };
